import React, { createContext, useCallback, useEffect, useMemo } from 'react';

import AgoraRTC, {
  AgoraRTCProvider,
  useJoin,
  usePublish,
  useRTCClient,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react';

import { useShallow } from 'zustand/react/shallow';

import { useWebinarStreamsHandler } from './hooks';
import SocketService from '@src/services/SocketService';

import { useMediaSettingsStore, useRoomActionsStore, useWebinarRoomStore } from '@src/stores';

import { EWebinarSockets } from '@src/constants';

interface WebinarRoomContextValue {
  localCameraTrack: ICameraVideoTrack | null;
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  webinarId: string;
  streamUID?: number;
  token: string;
  viewerIframeSrc: string;
  leaveChannel?: () => Promise<void>;
}

const defaultValue: WebinarRoomContextValue = {
  localCameraTrack: null,
  localMicrophoneTrack: null,
  webinarId: '',
  token: '',
  viewerIframeSrc: '',
  leaveChannel: async () => {},
};

export const WebinarRoomContext = createContext<WebinarRoomContextValue>(defaultValue);

export const WebinarRoomContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const rtcClient = useMemo(() => AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' }), []);
  const client = useRTCClient(rtcClient);

  return (
    <AgoraRTCProvider client={client}>
      <WebinarRoomContextInnerProvider>{children}</WebinarRoomContextInnerProvider>
    </AgoraRTCProvider>
  );
};

const WebinarRoomContextInnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // This gives us the same instance passed to AgoraRTCProvider
  const client = useRTCClient();

  const {
    webinarId = '',
    agoraStreamingData: { token = '', stream_uid: streamUID = 0 } = {},
    viewerIframeSrc = '',
    shareScreenStreamCredentials,
  } = useWebinarRoomStore(
    useShallow((state) => ({
      webinarId: state.webinarId,
      agoraStreamingData: state.agoraStreamingData,
      viewerIframeSrc: state.streamDetails?.viewerIframeSrc,
      shareScreenStreamCredentials: state.shareScreenStreamCredentials,
    }))
  );

  const { isMicClosed, isCamClosed } = useRoomActionsStore(
    useShallow((state) => ({
      isMicClosed: state.micSettings.isMicClosed,
      isCamClosed: state.camSettings.isCamClosed,
    }))
  );

  const { selectedAudioInputDevice } = useMediaSettingsStore(
    useShallow((state) => ({
      selectedAudioInputDevice: state.mediaSettings.selectedAudioInputDevice,
    }))
  );

  const { data } = useJoin(
    {
      appid: process.env.REACT_APP_AGORA_API_ID ?? '',
      channel: webinarId,
      uid: streamUID,
      token,
    },
    !!webinarId && !!streamUID && !!token
  );

  const isActive = useMemo(() => !!webinarId && !!streamUID && !!token && !!data, [data, streamUID, token, webinarId]);

  const { localCameraTrack } = useLocalCameraTrack(!isCamClosed && isActive);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(!isMicClosed && isActive, {
    microphoneId: selectedAudioInputDevice?.deviceId,
  });

  const leaveChannel = useCallback(async () => {
    try {
      localCameraTrack?.close();
      localMicrophoneTrack?.close();
      await client.leave();
      client.removeAllListeners();
    } catch (err) {
      console.error('Error during Agora cleanup:', err);
    }
  }, [client, localCameraTrack, localMicrophoneTrack]);

  useWebinarStreamsHandler({
    isActive,
    localCameraTrack,
    localMicrophoneTrack,
    shareScreenStreamCredentials,
  });

  usePublish([localCameraTrack, localMicrophoneTrack], isActive);

  // Agora connection and network diagnostics
  useEffect(() => {
    if (!client) return;

    let lastConnState: string | null = null;
    let lastUplink: number | null = null;
    let lastDownlink: number | null = null;

    const onConnectionStateChange = (curState: any, prevState: any, reason?: any) => {
      const cur = String(curState || '').toUpperCase();
      const prev = String(prevState || '').toUpperCase();
      const isProblem = cur === 'RECONNECTING' || cur === 'DISCONNECTING' || cur === 'DISCONNECTED';
      if (!isProblem) return;
      if (lastConnState === cur) return;
      lastConnState = cur;
      console.warn('[Agora] connection-state-change', { prevState: prev, curState: cur, reason });
    };

    const onNetworkQuality = (stats: any) => {
      const u = Number(stats?.uplinkNetworkQuality);
      const d = Number(stats?.downlinkNetworkQuality);
      // Consider 2 (fair) as degraded to surface issues under lighter throttling
      const uplinkBad = Number.isFinite(u) && u >= 2;
      const downlinkBad = Number.isFinite(d) && d >= 2;
      const uplinkChangedToBad = uplinkBad && lastUplink !== u;
      const downlinkChangedToBad = downlinkBad && lastDownlink !== d;
      if (uplinkChangedToBad || downlinkChangedToBad) {
        console.warn('[Agora] network-quality degraded', { uplinkNetworkQuality: u, downlinkNetworkQuality: d });
      }
      lastUplink = u;
      lastDownlink = d;
    };

    const onException = (evt: any) => {
      console.warn('[Agora] exception', evt);
    };

    const onUserOffline = (uid: any, reason: any) => {
      console.warn('[Agora] user-offline', { uid, reason });
    };

    client.on('connection-state-change', onConnectionStateChange);
    client.on('network-quality', onNetworkQuality);
    client.on('exception', onException);
    client.on('user-left', onUserOffline);

    return () => {
      client.off('connection-state-change', onConnectionStateChange);
      client.off('network-quality', onNetworkQuality);
      client.off('exception', onException);
      client.off('user-left', onUserOffline);
    };
  }, [client]);

  useEffect(() => {
    const switchMicrophone = async () => {
      if (selectedAudioInputDevice?.deviceId && localMicrophoneTrack) {
        try {
          await localMicrophoneTrack.setDevice(selectedAudioInputDevice.deviceId);
        } catch (error) {
          console.error('Failed to switch microphone:', error);
        }
      }
    };
    switchMicrophone();
  }, [selectedAudioInputDevice, localMicrophoneTrack]);

  useEffect(() => {
    if (!isActive || !streamUID || !webinarId) return;
    SocketService.emit(EWebinarSockets.HOST_STREAM_START, { roomId: webinarId, uid: streamUID });
    return () => {
      if (isActive && streamUID && webinarId) {
        SocketService.emit(EWebinarSockets.HOST_STREAM_STOP, { roomId: webinarId, uid: streamUID });
      }
    };
  }, [isActive, streamUID, webinarId]);

  const contextValue = useMemo(
    () => ({
      localCameraTrack,
      localMicrophoneTrack,
      webinarId,
      streamUID,
      token,
      viewerIframeSrc,
      leaveChannel,
    }),
    [localCameraTrack, localMicrophoneTrack, webinarId, streamUID, token, viewerIframeSrc, leaveChannel]
  );

  return <WebinarRoomContext.Provider value={contextValue}>{children}</WebinarRoomContext.Provider>;
};
